import CloseIcon from "@mui/icons-material/Close";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Typography } from "@mui/material";

/**
 * ConfirmWorkforceModal
 *
 * Props:
 *  - open        {boolean}   Controls modal visibility
 *  - onClose     {function}  Called when the modal should close (Cancel or backdrop click)
 *  - onConfirm   {function}  Called when the user confirms the action
 */
export default function ConfirmWorkforceModal({ open, onClose, onConfirm, isApplicant = false }: { open: boolean; onClose: () => void; onConfirm: () => void; isApplicant?: boolean }) {
  const handleConfirm = () => {
    onClose(); // close the modal first
    onConfirm(); // then execute the action
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disablePortal={true}
      PaperProps={{
        elevation: 6,
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          zIndex: 999999999999999999999999,
        },
      }}
    >
      {/* Coloured top accent bar */}
      <Box
        sx={{
          height: 6,
          background: "linear-gradient(90deg, #f59e0b 0%, #ef4444 100%)",
        }}
      />

      {/* Close button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pt: 1.5 }}>
        <IconButton size="small" onClick={onClose} aria-label="Close modal">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Icon + Title */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          px: 4,
          pt: 1,
          pb: 2,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            bgcolor: "warning.50",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
            border: "2px solid",
            borderColor: "warning.200",
          }}
        >
          <PersonAddAlt1Icon sx={{ fontSize: 32, color: "warning.main" }} />
        </Box>

        <DialogTitle
          component="h2"
          sx={{
            p: 0,
            fontSize: "1.25rem",
            fontWeight: 700,
            lineHeight: 1.35,
            color: "text.primary",
          }}
        >
          You are about to create a new {isApplicant ? "applicant" : "workforce member"}, are you sure?
        </DialogTitle>
      </Box>

      <Divider />

      {/* Subheader / body */}
      <DialogContent sx={{ px: 4, py: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            bgcolor: "warning.50",
            border: "1px solid",
            borderColor: "warning.200",
            borderRadius: 2,
            p: 2,
          }}
        >
          <WarningAmberRoundedIcon sx={{ color: "warning.main", mt: 0.2, flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary" lineHeight={1.65}>
            This action will consume one of your available seats. Please check all the details before confirming this action.
          </Typography>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          px: 4,
          pb: 3,
          pt: 1,
          gap: 1.5,
          flexDirection: { xs: "column-reverse", sm: "row" },
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          fullWidth
          onClick={onClose}
          sx={{
            borderColor: "divider",
            color: "text.secondary",
            fontWeight: 600,
            textTransform: "none",
            borderRadius: 2,
            py: 1.1,
            "&:hover": { borderColor: "text.secondary", bgcolor: "action.hover" },
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          fullWidth
          onClick={handleConfirm}
          sx={{
            background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
            color: "#fff",
            fontWeight: 700,
            textTransform: "none",
            borderRadius: 2,
            py: 1.1,
            boxShadow: "0 4px 14px rgba(239,68,68,0.35)",
            "&:hover": {
              background: "linear-gradient(135deg, #d97706 0%, #dc2626 100%)",
              boxShadow: "0 6px 18px rgba(239,68,68,0.45)",
            },
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
